import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Bookmark, ArrowLeft, X, Map, List } from "lucide-react";
import api, { errMessage } from "../lib/api";
import Layout from "../components/Layout";
import PropertyCard from "../components/PropertyCard";
import PropertyDetailModal from "../components/PropertyDetailModal";
import AIInsightsPanel from "../components/AIInsightsPanel";
import MarketScoreCard from "../components/MarketScoreCard";
import MarketHealthPanel from "../components/MarketHealthPanel";
import MapView from "../components/MapView";
import { PROPERTY_TYPES, filterByTypes } from "../lib/propertyTypes";
import { DISTRESS_SIGNALS, signalLabel, filterBySignals } from "../lib/distressSignals";

export default function MarketDetail() {
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const county = params.get("county") || "";
  const state = params.get("state") || "";
  const typesParam = params.get("types") || "";
  const signalsParam = params.get("signals") || "";
  const selectedTypes = useMemo(
    () => typesParam.split(",").map((s) => s.trim()).filter(Boolean),
    [typesParam],
  );
  const selectedSignals = useMemo(
    () => signalsParam.split(",").map((s) => s.trim()).filter(Boolean),
    [signalsParam],
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProp, setActiveProp] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    if (!city || !state) return;
    setLoading(true);
    api
      .post("/markets/analyze", { city, county, state })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMessage(e, "Analysis failed")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- module-level api/errMessage are stable
  }, [city, county, state]);

  const removeType = (id) => {
    const next = selectedTypes.filter((t) => t !== id);
    const newParams = new URLSearchParams(params);
    if (next.length === 0) newParams.delete("types");
    else newParams.set("types", next.join(","));
    setParams(newParams, { replace: true });
  };

  const clearTypes = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete("types");
    setParams(newParams, { replace: true });
  };

  const removeSignal = (id) => {
    const next = selectedSignals.filter((s) => s !== id);
    const newParams = new URLSearchParams(params);
    if (next.length === 0) newParams.delete("signals");
    else newParams.set("signals", next.join(","));
    setParams(newParams, { replace: true });
  };

  const clearSignals = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete("signals");
    setParams(newParams, { replace: true });
  };

  const clearAllFilters = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete("types");
    newParams.delete("signals");
    setParams(newParams, { replace: true });
  };

  const saveSearch = async () => {
    try {
      await api.post("/saved/searches", { city, county, state });
      toast.success("Search saved");
    } catch (e) {
      toast.error(errMessage(e, "Could not save"));
    }
  };

  const saveDeal = async (prop) => {
    try {
      await api.post("/saved/deals", {
        property_id: prop.id,
        property_data: prop,
        notes: "",
      });
      toast.success("Deal saved");
    } catch (e) {
      toast.error(errMessage(e, "Could not save"));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-12 flex items-center gap-3 text-sm text-[--ink-muted]">
          <div className="w-2 h-2 bg-[--accent] animate-pulse" />
          Pulling market data for {city}…
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-12">
          <Link to="/dashboard" data-testid="back-to-dashboard" className="text-sm text-[--accent]">
            ← Back to search
          </Link>
        </div>
      </Layout>
    );
  }

  const { market, data_source, from_cache } = data;
  const allProperties = data.properties || [];
  const properties = filterBySignals(
    filterByTypes(allProperties, selectedTypes),
    selectedSignals,
  );
  const isLive = data_source && data_source.startsWith("live");

  return (
    <Layout>
      <div className="border-b border-[--line] bg-white">
        <div className="px-8 py-5 flex items-center justify-between">
          <div>
            <Link
              to="/dashboard"
              data-testid="back-to-dashboard"
              className="overline inline-flex items-center gap-1.5 mb-2 hover:text-[--ink]"
            >
              <ArrowLeft size={12} /> Back
            </Link>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              {market.city}, {market.state}
              <span className="text-[--ink-muted] ml-3 text-base font-mono">
                · {market.county} County
              </span>
            </h1>
            <div
              data-testid="data-source-badge"
              className={`inline-flex items-center gap-1.5 mt-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                isLive
                  ? "border-[--success] text-[--success] bg-green-50"
                  : "border-[--line] text-[--ink-muted] bg-[--bg-soft]"
              }`}
            >
              <span className={`w-1.5 h-1.5 ${isLive ? "bg-[--success]" : "bg-[--ink-muted]"}`} />
              {isLive ? data_source.replace("live:", "Live · ") : "Synthetic data"}
              {from_cache && <span className="ml-1 opacity-60">(cached)</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="save-search-button"
              onClick={saveSearch}
              className="btn-ghost flex items-center gap-2"
            >
              <Bookmark size={14} strokeWidth={1.5} /> Save search
            </button>
            <button
              data-testid="open-ai-insights-button"
              onClick={() => setAiOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Sparkles size={14} strokeWidth={1.5} /> AI insights
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 max-w-7xl">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[--line] mb-8 bg-white">
          <MarketScoreCard
            testid="metric-opportunity"
            label="Opportunity"
            value={market.opportunity_score}
            unit="/100"
            tone="primary"
            sub="composite score"
          />
          <MarketScoreCard
            testid="metric-motivation"
            label="Motivation index"
            value={market.motivation_index}
            sub="seller distress density"
          />
          <MarketScoreCard
            testid="metric-saturation"
            label="Saturation"
            value={market.saturation_score}
            invert
            sub="lower = better"
          />
          <MarketScoreCard
            testid="metric-cash"
            label="Cash landlord %"
            value={market.cash_landlord_pct}
            unit="%"
            sub="of recent closings"
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="surface-card p-5">
            <div className="overline mb-3">Distress signals (last 90d)</div>
            <div className="space-y-3">
              <Stat label="Pre-foreclosures" value={market.pre_foreclosures} tone="danger" />
              <Stat label="Code violations" value={market.code_violations} tone="warn" />
              <Stat label="Recent evictions" value={market.recent_evictions} tone="danger" />
            </div>
          </div>
          <div className="surface-card p-5">
            <div className="overline mb-3">Market economics</div>
            <div className="space-y-3">
              <Stat label="Median ARV" value={`$${market.median_arv.toLocaleString()}`} />
              <Stat label="Median rehab" value={`$${market.median_rehab.toLocaleString()}`} />
              <Stat label="Median DOM" value={`${market.median_dom} days`} />
              <Stat label="Avg owner equity" value={`${market.avg_equity_pct}%`} />
            </div>
          </div>
          <div className="surface-card p-5">
            <div className="overline mb-3">Investor competition</div>
            <div className="text-5xl font-display font-black tracking-tighter mb-2">
              {market.investor_competition}
              <span className="text-base text-[--ink-muted] font-mono ml-1">/100</span>
            </div>
            <div className="score-bar mb-3">
              <span style={{ width: `${market.investor_competition}%` }} />
            </div>
            <p className="text-xs text-[--ink-soft] leading-relaxed">
              Lower numbers mean fewer investors are bidding, hunting, or wholesaling here right now.
              You want this number under 40.
            </p>
          </div>
        </div>

        <MarketHealthPanel
          velocity={market.sales_velocity}
          pop={market.population_trend}
          emp={market.employment_trend}
          bonus={market.health_bonus}
        />

        <div className="surface-card mb-10">
          <div className="border-b border-[--line] px-5 py-3 flex items-center justify-between">
            <div className="overline">Top sub-markets · ranked by opportunity</div>
            <div className="text-xs font-mono text-[--ink-muted]">{market.submarkets.length} zones</div>
          </div>
          <table className="w-full text-sm" data-testid="submarkets-table">
            <thead>
              <tr className="border-b border-[--line] text-xs text-[--ink-muted] uppercase tracking-wider">
                <th className="text-left py-3 px-4 font-medium">Zone</th>
                <th className="text-left py-3 px-4 font-medium">ZIP</th>
                <th className="text-right py-3 px-4 font-medium">Saturation</th>
                <th className="text-right py-3 px-4 font-medium">Motivation</th>
                <th className="text-right py-3 px-4 font-medium">Median price</th>
                <th className="text-right py-3 px-4 font-medium">Deals</th>
              </tr>
            </thead>
            <tbody>
              {market.submarkets.map((s, i) => (
                <tr key={s.zip} className="border-b border-[--line] hover:bg-[--bg-soft]">
                  <td className="py-3 px-4">{s.neighborhood}</td>
                  <td className="py-3 px-4 font-mono text-[--ink-muted]">{s.zip}</td>
                  <td className="py-3 px-4 text-right font-mono">{s.saturation}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold">{s.motivation}</td>
                  <td className="py-3 px-4 text-right font-mono">${s.median_price.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono">{s.deal_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="overline mb-1">// 02 · Deal queue</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              {properties.length} of {allProperties.length} properties
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="inline-flex border border-[--line] bg-[--bg-soft] p-0.5">
              <button
                type="button"
                data-testid="view-mode-list"
                onClick={() => setViewMode("list")}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                  viewMode === "list" ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                }`}
              >
                <List size={12} strokeWidth={1.5} /> List
              </button>
              <button
                type="button"
                data-testid="view-mode-map"
                onClick={() => setViewMode("map")}
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 ${
                  viewMode === "map" ? "bg-[--ink] text-white" : "text-[--ink-soft] hover:text-[--ink]"
                }`}
              >
                <Map size={12} strokeWidth={1.5} /> Map
              </button>
            </div>
            <div className="text-xs text-[--ink-muted] font-mono">
              sorted by opportunity
            </div>
          </div>
        </div>

        {(selectedTypes.length > 0 || selectedSignals.length > 0) && (
          <div data-testid="active-filters" className="flex items-center gap-1.5 flex-wrap mb-4 text-xs">
            {selectedTypes.length > 0 && (
              <>
                <span className="font-mono text-[--ink-muted] uppercase tracking-wider mr-1">Types:</span>
                {selectedTypes.map((id) => {
                  const t = PROPERTY_TYPES.find((x) => x.id === id);
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid={`type-chip-${id}`}
                      onClick={() => removeType(id)}
                      className="inline-flex items-center gap-1 bg-[--ink] text-white px-2 py-0.5 hover:bg-[--ink-soft]"
                    >
                      {t?.label || id}
                      <X size={10} strokeWidth={2} />
                    </button>
                  );
                })}
              </>
            )}
            {selectedSignals.length > 0 && (
              <>
                {selectedTypes.length > 0 && <span className="text-[--ink-muted] mx-1">·</span>}
                <span className="font-mono text-[--ink-muted] uppercase tracking-wider mr-1">Signals:</span>
                {selectedSignals.map((id) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`signal-chip-${id}`}
                    onClick={() => removeSignal(id)}
                    className="inline-flex items-center gap-1 bg-[--accent] text-white px-2 py-0.5 hover:opacity-80"
                  >
                    {signalLabel(id)}
                    <X size={10} strokeWidth={2} />
                  </button>
                ))}
              </>
            )}
            <button
              type="button"
              data-testid="clear-filters"
              onClick={clearAllFilters}
              className="text-[--ink-muted] hover:text-[--ink] underline ml-2"
            >
              clear all
            </button>
          </div>
        )}

        {viewMode === "map" ? (
          <MapView properties={properties} onOpenProperty={setActiveProp} />
        ) : properties.length === 0 ? (
          <div
            data-testid="empty-filtered-state"
            className="border border-dashed border-[--line] p-12 text-center text-sm text-[--ink-muted]"
          >
            No properties match your filters.
            <button
              type="button"
              onClick={clearAllFilters}
              className="ml-2 text-[--ink] underline hover:text-[--accent]"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="property-grid">
            {properties.map((p) => (
              <PropertyCard
                key={p.id}
                prop={p}
                onOpen={() => setActiveProp(p)}
                onSave={() => saveDeal(p)}
              />
            ))}
          </div>
        )}
      </div>

      {activeProp && (
        <PropertyDetailModal
          prop={activeProp}
          onClose={() => setActiveProp(null)}
          onSave={() => {
            saveDeal(activeProp);
            setActiveProp(null);
          }}
        />
      )}

      {aiOpen && (
        <AIInsightsPanel
          market={market}
          properties={properties}
          city={city}
          county={county}
          state={state}
          onClose={() => setAiOpen(false)}
        />
      )}
    </Layout>
  );
}

function Stat({ label, value, tone }) {
  const dot =
    tone === "danger" ? "bg-[--danger]" : tone === "warn" ? "bg-[--warn]" : "bg-[--ink]";
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-1.5 ${dot}`} />
        <span className="text-sm text-[--ink-soft]">{label}</span>
      </div>
      <span className="font-mono font-medium">{value}</span>
    </div>
  );
}
